import { useMemo, useRef } from 'react';
import { useInfiniteQuery, InfiniteData, useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { useAtomValue } from 'jotai';

import { QUERY_KEYS } from '@restworld/utils-common';
import { useRestaurantsService, IndexRestaurants, ApplicantsAvatar } from '@restworld/data-services';
import { Restaurant } from '@restworld/utility-types';
import { useAuth } from '@restworld/feature-authentication';

import {
  employerRestaurantJpStatusFilterAtom,
  employerRestaurantLocationFilterAtom,
  restaurantSearchQueryAtom,
  restaurantSortAtom,
} from '../employer-restaurants-filters/employer-restaurants.atoms';

const DEFAULT_OFFSET = 0;

export const useQueryFilterOptions = () => {
  const searchQuery = useAtomValue(restaurantSearchQueryAtom);
  const jpStatus = useAtomValue(employerRestaurantJpStatusFilterAtom);
  const location = useAtomValue(employerRestaurantLocationFilterAtom);
  const restaurantSortValue = useAtomValue(restaurantSortAtom);
  const restaurantQueryOptions = useMemo(() => {
    const filters = {
      search_vector: searchQuery,
      ...(location ? { location: [location] } : {}),
      only_active: getOpenJpsValue(jpStatus),
    };

    return {
      ...(restaurantSortValue ? { order_by: [restaurantSortValue] } : {}),
      filters,
    };
  }, [restaurantSortValue, searchQuery, location, jpStatus]);
  return restaurantQueryOptions;
};
export const useFetchEmployerRestaurantsQueryKey = () => {
  const { user } = useAuth();
  const queryFilterOptions = useQueryFilterOptions();
  const { order_by, filters } = queryFilterOptions;
  const organizationId = user?.organization_id;
  const filtersKey = useMemo(() => (filters ? JSON.stringify(filters) : undefined), [filters]);
  const orderKey = useMemo(() => (order_by ? JSON.stringify(order_by) : undefined), [order_by]);
  return [QUERY_KEYS.RESTAURANTS.LIST, organizationId, orderKey, filtersKey] as const;
};
const limit = 10;
function useEmployerRestaurantsQuery({ enabled = true }: { enabled?: boolean } = {}) {
  const queryFilterOptions = useQueryFilterOptions();
  const { order_by, filters } = queryFilterOptions;

  const restaurantsService = useRestaurantsService();
  const { user } = useAuth();
  const organizationId = user?.organization_id;
  const nextPageRef = useRef<number>();
  const hasNextPageRef = useRef<boolean>();

  const queryKey = useFetchEmployerRestaurantsQueryKey();
  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) => {
      nextPageRef.current = pageParam + 1;
      return restaurantsService.fetchRestaurants({
        limit,
        offset: pageParam * limit,
        order_by,
        filters: {
          organization_id: organizationId,
          ...filters,
        },
      });
    },
    getNextPageParam: (lastPage) => {
      const totalHits = Array.isArray(lastPage.data) ? lastPage.data.length : lastPage.data.hits;
      const restaurantsLoadedSoFar = nextPageRef.current * limit;

      hasNextPageRef.current = restaurantsLoadedSoFar < totalHits;
      return nextPageRef.current;
    },
    refetchOnWindowFocus: false,
    initialPageParam: DEFAULT_OFFSET,
    enabled,
  });

  const restaurantsInfo = useMemo(() => extractRestaurantsInfo(query.data), [query.data]);

  return {
    query,
    restaurants: restaurantsInfo.restaurantData,
    hits: restaurantsInfo.hits,
    hasNextPage: hasNextPageRef.current,
  };
}
export const useEmployerRestaurantsData = () =>
  useQueryClient().getQueryData(useFetchEmployerRestaurantsQueryKey()) as InfiniteData<
    AxiosResponse<IndexRestaurants, unknown>
  >;

export default useEmployerRestaurantsQuery;

export const getOpenJpsValue = (jpStatusFilter: string[]) => {
  if (jpStatusFilter?.length !== 1) return undefined;
  if (jpStatusFilter.includes('open')) return true;
  if (jpStatusFilter.includes('closed')) return false;
  throw new Error('Invalid jpStatusFilter value');
};

export type RestaurantInfo = {
  restaurant: Restaurant;
  notViewedNormalJobApplicantsAvatar?: ApplicantsAvatar[];
  notViewedNormalJobApplicationCount?: number;
  notViewedSpontaneousApplicantsAvatar?: ApplicantsAvatar[];
  notViewedSpontaneousApplicationCount?: number;
  numberOfJobPositions?: number;
};

type RestaurantsData = {
  restaurantData: RestaurantInfo[];
  hits: number | null;
};

export const extractRestaurantsInfo = (
  data: InfiniteData<AxiosResponse<IndexRestaurants, unknown>>
): RestaurantsData => {
  if (!data || !data.pages) return { restaurantData: [], hits: null };

  let hits: number | null = null;

  const restaurantData = data.pages.flatMap((page) => {
    const pageData = page.data;

    if ('data' in pageData) {
      hits = pageData.hits;
      return pageData.data.map((item) => ({
        restaurant: item.restaurant,
        notViewedNormalJobApplicantsAvatar: item.not_viewed_normal_job_applicants_avatar,
        notViewedNormalJobApplicationCount: item.not_viewed_normal_job_applications_count,
        notViewedSpontaneousApplicantsAvatar: item.not_viewed_spontaneous_applicants_avatar,
        notViewedSpontaneousApplicationCount: item.not_viewed_spontaneous_applications_count,
        numberOfJobPositions: item.number_of_job_positions,
      }));
    }

    return [];
  });

  return {
    restaurantData,
    hits,
  };
};
